import { Link } from "react-router-dom";
import {
  Phone, Award, ShieldCheck, BadgeCheck, Star, Building2,
  ArrowRight, Sparkles, Hammer, Brush, Quote
} from "lucide-react";
import { SITE, HERO_BG, TESTIMONIALS, SERVICES } from "../lib/site";
import TrustBadges from "../components/TrustBadges";
import BeforeAfter from "../components/BeforeAfter";
import { BEFORE_AFTER, OWNER_PORTRAIT } from "../lib/site";

const WHY = [
  { icon: Award, title: "10 Years of Mastery", text: "A decade of craftsmanship across hundreds of homes in Wesley Chapel." },
  { icon: ShieldCheck, title: "1 Year Warranty", text: "Workmanship guaranteed in writing on every job, big or small." },
  { icon: BadgeCheck, title: "Honest, Detailed Quotes", text: "Transparent estimates — no hidden fees, no surprises." },
  { icon: Sparkles, title: "Premium Materials", text: "Designer-grade finishes that protect and elevate your investment." },
  { icon: Building2, title: "Residential & Commercial", text: "Equipped and insured for projects of any size and scope." },
  { icon: Star, title: "5-Star Reviews", text: "Repeat customers and referrals fuel everything we do." },
];

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center grain" data-testid="hero-section">
        <div
          className="hero-bg"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black" />
        <div className="container-luxury relative z-10 py-24">
          <div className="max-w-4xl">
            <p className="overline fade-up" data-testid="hero-overline">Wesley Chapel, FL · Est. 2016</p>
            <h1
              className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light leading-[1.05] text-white fade-up fade-up-delay-1"
              data-testid="hero-headline"
            >
              Welcome to <br />
              <span className="font-script text-[var(--gold)] font-medium">Zelaya's</span> Design INC
            </h1>
            <p className="mt-8 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed fade-up fade-up-delay-2" data-testid="hero-subheadline">
              Professional Painting, Stucco &amp; Home Improvement Services.
              Serving Wesley Chapel and surrounding areas with quality, professionalism and
              <span className="text-white"> guaranteed workmanship</span>.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 fade-up fade-up-delay-3">
              <Link to="/contact" className="btn-primary" data-testid="hero-cta-estimate">
                Get Free Estimate <ArrowRight size={16} />
              </Link>
              <a href={SITE.phoneHref} className="btn-secondary" data-testid="hero-cta-call">
                <Phone size={16} /> Call Now
              </a>
              <a
                href={SITE.googleReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost"
                data-testid="hero-cta-review"
              >
                <Star size={16} /> Leave a Review
              </a>
            </div>
          </div>
        </div>
      </section>

      <TrustBadges />

      {/* Why choose us */}
      <section className="section" data-testid="why-choose-us">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-16">
            <div className="lg:col-span-7">
              <p className="overline">Why Choose Us</p>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-white font-light leading-[1.05]">
                Craft. Detail. <br />
                <span className="text-[var(--gold)] font-script">Integrity.</span>
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-white/60 text-lg leading-relaxed">
                Every wall, every coat, every line — finished with the precision of a craftsman who actually
                shows up. We treat your home the way we would treat our own.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {WHY.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-[#0A0A0A] p-10 group hover:bg-[#141414] transition-colors" data-testid={`why-card-${i}`}>
                  <Icon size={32} className="text-[var(--gold)]" />
                  <h3 className="mt-6 font-display text-2xl text-white">{item.title}</h3>
                  <p className="mt-3 text-white/55 leading-relaxed">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured services preview */}
      <section className="section bg-[#0c0c0c]" data-testid="home-services-preview">
        <div className="container-luxury">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <p className="overline">Services</p>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl text-white font-light leading-tight">
                A complete <span className="font-script text-[var(--gold)]">design &amp; build</span> studio
              </h2>
            </div>
            <Link to="/services" className="link-underline text-white/80 hover:text-white text-sm uppercase tracking-[0.2em]" data-testid="view-all-services">
              View all services →
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
            {SERVICES.slice(0, 6).map((s, i) => (
              <Link key={s.slug} to="/services" className="bg-[#0A0A0A] hover:bg-[#141414] p-8 group" data-testid={`home-service-${s.slug}`}>
                <div className="flex items-start justify-between">
                  <Brush size={22} className="text-[var(--gold)]" />
                  <ArrowRight size={18} className="text-white/30 group-hover:text-[var(--gold)] group-hover:translate-x-1 transition-all" />
                </div>
                <h3 className="mt-8 font-display text-2xl text-white">{s.title}</h3>
                <p className="mt-3 text-white/55 text-sm leading-relaxed">{s.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Before / after */}
      <section className="section" data-testid="home-before-after">
        <div className="container-luxury grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <p className="overline">Before · After</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl text-white font-light leading-[1.05]">
              Real homes. <br /> <span className="font-script text-[var(--gold)]">Real results.</span>
            </h2>
            <p className="mt-6 text-white/60 leading-relaxed">
              Slide to see the transformation. From tired exteriors to designer-grade finishes — we restore
              the value, character and pride of your property.
            </p>
            <Link to="/gallery" className="mt-8 btn-secondary inline-flex" data-testid="home-view-gallery">
              View Full Gallery <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-7">
            <BeforeAfter before={BEFORE_AFTER.before} after={BEFORE_AFTER.after} />
          </div>
        </div>
      </section>

      {/* Owner band */}
      <section className="section bg-[#0c0c0c]" data-testid="home-owner-band">
        <div className="container-luxury grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] overflow-hidden border border-white/5">
              <img src={OWNER_PORTRAIT} alt="Santos Dario Zelaya" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-6 hidden md:block bg-[var(--gold)] text-black px-6 py-4 font-display text-xl">
              10 Years
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="overline">The Owner</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl text-white font-light leading-tight">
              Meet <span className="font-script text-[var(--gold)]">Santos Dario Zelaya</span>
            </h2>
            <Quote size={36} className="text-[var(--gold)] mt-8 opacity-60" />
            <p className="mt-4 text-xl text-white/80 leading-relaxed italic max-w-2xl">
              "Every home I touch carries my name. That's why we measure twice, paint clean lines,
              and finish what we start. Honesty and craft — that's the standard."
            </p>
            <p className="mt-4 font-script text-3xl text-[var(--gold)]">— Santos</p>
            <Link to="/about" className="mt-8 inline-flex btn-secondary" data-testid="home-about-cta">
              Read Our Story <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonial highlight */}
      <section className="section" data-testid="home-testimonial-highlight">
        <div className="container-luxury">
          <div className="max-w-4xl mx-auto text-center">
            <p className="overline">5-Star Reviews</p>
            <div className="mt-6 flex justify-center gap-1">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={20} fill="#C5A059" className="text-[var(--gold)]" />
              ))}
            </div>
            <p className="mt-8 font-display text-2xl md:text-3xl text-white leading-snug italic">
              "{TESTIMONIALS[0].quote}"
            </p>
            <p className="mt-6 text-white/60 uppercase tracking-[0.2em] text-sm">
              {TESTIMONIALS[0].name} · {TESTIMONIALS[0].location}
            </p>
            <Link to="/reviews" className="mt-8 inline-flex btn-secondary" data-testid="home-reviews-cta">
              Read All Reviews
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative section bg-[#0c0c0c]" data-testid="home-final-cta">
        <div className="container-luxury text-center">
          <Hammer className="mx-auto text-[var(--gold)]" size={36} />
          <h2 className="mt-6 font-display text-4xl md:text-6xl text-white font-light">
            Ready to <span className="font-script text-[var(--gold)]">transform</span> your space?
          </h2>
          <p className="mt-6 text-white/60 text-lg max-w-2xl mx-auto">
            Get a free, honest estimate today. We'll show up on time, leave the site clean, and warranty
            our work for a full year.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-primary" data-testid="final-cta-estimate">
              Request Free Estimate <ArrowRight size={16} />
            </Link>
            <a href={SITE.phoneHref} className="btn-secondary" data-testid="final-cta-call">
              <Phone size={16} /> {SITE.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
